import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Users, Star } from 'lucide-react';
import { db } from '../firebase';
import { collection, onSnapshot } from 'firebase/firestore';

export default function LandingPage() {
  const [stats, setStats] = useState({ animals: 0, donors: 0, couriers: 0, done: 0 });

  // Real-time stats dari Firestore
  useEffect(() => {
    const u1 = onSnapshot(collection(db, 'animals'), s => {
      setStats(p => ({ ...p, animals: s.size }));
    });
    const u2 = onSnapshot(collection(db, 'donors'), s => {
      setStats(p => ({
        ...p,
        donors: s.size,
        done: s.docs.filter(d => d.data().distributionStatus === 'selesai').length
      }));
    });
    const u3 = onSnapshot(collection(db, 'couriers'), s => {
      setStats(p => ({ ...p, couriers: s.docs.filter(d => d.data().status === 'active').length }));
    });
    return () => { u1(); u2(); u3(); };
  }, []);

  const features = [
    { icon: '🐄', title: 'Data Hewan Qurban', desc: 'Pencatatan lengkap sapi & kambing beserta kondisi dan status penyembelihan.' },
    { icon: '🔪', title: 'Proses Penyembelihan', desc: 'Monitoring real-time progres penyembelihan dengan persentase penyelesaian.' },
    { icon: '📦', title: 'Packing Daging', desc: 'Tracking proses pemotongan dan pengemasan daging per hewan.' },
    { icon: '🚚', title: 'Distribusi & Pengiriman', desc: 'Pantau kurir secara live saat mengantarkan daging ke penerima.' },
    { icon: '👥', title: 'Data Pengkurban', desc: 'Daftar lengkap nama pengkurban beserta status distribusi daging.' },
    { icon: '📱', title: 'Akses Publik', desc: 'Semua masyarakat bisa memantau proses tanpa perlu login.' },
  ];

  return (
    <div style={{ paddingTop: 64 }}>
      <style>{`
        .hero-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 3rem; align-items: center; }
        .steps-row { display: flex; gap: 0.75rem; justify-content: center; flex-wrap: wrap; }
        .step-card { width: 160px; text-align: center; padding: 1.25rem 0.75rem; }
        .step-arrow { color: #86efac; font-size: 20px; align-self: center; }
        @media (max-width: 768px) {
          .hero-grid { grid-template-columns: 1fr; gap: 2rem; }
          .step-arrow { display: none; }
          .step-card { width: 140px; padding: 1rem 0.5rem; }
        }
        @media (max-width: 480px) {
          .step-card { width: 120px; }
        }
      `}</style>

      {/* ── HERO ── */}
      <section style={{
        minHeight: '92vh', display: 'flex', alignItems: 'center',
        background: 'linear-gradient(160deg,#14532d 0%,#166534 45%,#15803d 100%)',
        position: 'relative', overflow: 'hidden', padding: '5rem 1.25rem 3rem'
      }}>
        <div style={{ position:'absolute', top:'-15%', right:'-8%', width:500, height:500, borderRadius:'50%', background:'rgba(255,255,255,0.03)', pointerEvents:'none' }} />
        <div style={{ position:'absolute', bottom:'-20%', left:'-5%', width:400, height:400, borderRadius:'50%', background:'rgba(251,191,36,0.05)', pointerEvents:'none' }} />

        <div style={{ maxWidth: 1100, margin: '0 auto', width: '100%' }}>
          <div className="hero-grid">

            {/* Left — copy */}
            <div>
              {/* <div className="arabic" style={{ fontSize: 'clamp(20px,4vw,28px)', color:'#fbbf24', marginBottom:12, opacity:0.9 }}>
                بِسْمِ اللهِ الرَّحْمَنِ الرَّحِيم
              </div> */}
              <div style={{ display:'inline-flex', alignItems:'center', gap:6, background:'rgba(251,191,36,0.15)', border:'1px solid rgba(251,191,36,0.3)', borderRadius:999, padding:'5px 14px', marginBottom:18 }}>
                <Star size={12} fill="#fbbf24" color="#fbbf24" />
                <span style={{ color:'#fbbf24', fontSize:13, fontWeight:600 }}>Program Qurma 1447 H</span>
              </div>
              <h1 style={{ fontSize:'clamp(1.75rem,5vw,3rem)', color:'#fff', lineHeight:1.2, marginBottom:'1rem' }}>
                Sistem Monitoring<br />
                <span style={{ color:'#86efac' }}>Qurban Transparan</span><br />
                &amp; Terpercaya
              </h1>
              <p style={{ color:'#a7d9b8', fontSize:'clamp(14px,2vw,16px)', lineHeight:1.75, marginBottom:'1.75rem', maxWidth:460 }}>
                Pantau setiap tahap proses qurban secara real-time — dari penyembelihan, packing daging, hingga distribusi.
              </p>
              <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
                <Link to="/monitoring" className="btn btn-gold" style={{ fontSize:15, padding:'12px 24px' }}>
                  Lihat Monitoring <ArrowRight size={15} />
                </Link>
                <Link to="/pengkurban" className="btn" style={{ background:'rgba(255,255,255,0.1)', color:'#fff', border:'1px solid rgba(255,255,255,0.25)', fontSize:15, padding:'12px 24px' }}>
                  <Users size={15} /> Nama Pengkurban
                </Link>
              </div>
            </div>

            {/* Right — live stats card */}
            <div style={{ background:'rgba(255,255,255,0.07)', border:'1px solid rgba(255,255,255,0.12)', borderRadius:20, padding:'1.75rem', backdropFilter:'blur(10px)' }}>
              <div style={{ color:'#86efac', fontSize:12, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:14 }}>
                Program Tahun Ini
              </div>
              {[
                { label:'Total Hewan Qurban', value: stats.animals || '—', icon:'🐄', color:'#86efac' },
                { label:'Pengkurban Terdaftar', value: stats.donors || '—', icon:'👥', color:'#fbbf24' },
                { label:'Sudah Terima Daging', value: stats.done || '—', icon:'✅', color:'#a5b4fc' },
                { label:'Kurir Aktif Saat Ini', value: stats.couriers || '—', icon:'🚚', color:'#fb923c' },
              ].map((item, i) => (
                <div key={i} style={{ display:'flex', alignItems:'center', gap:12, padding:'11px 0', borderBottom: i < 3 ? '1px solid rgba(255,255,255,0.07)' : 'none' }}>
                  <span style={{ fontSize:22, width:32, textAlign:'center' }}>{item.icon}</span>
                  <div style={{ flex:1 }}>
                    <div style={{ color:'rgba(255,255,255,0.55)', fontSize:12 }}>{item.label}</div>
                    <div style={{ color:item.color, fontSize:22, fontWeight:700, lineHeight:1.2 }}>{item.value}</div>
                  </div>
                </div>
              ))}
              <div style={{ marginTop:14, padding:'10px 14px', background:'rgba(251,191,36,0.08)', borderRadius:10, border:'1px solid rgba(251,191,36,0.18)' }}>
                <div style={{ color:'#fbbf24', fontSize:12, fontWeight:600 }}>🔄 Data diperbarui otomatis</div>
                <div style={{ color:'rgba(255,255,255,0.4)', fontSize:11, marginTop:2 }}>Tersinkronisasi langsung dari database</div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ── ALUR PROSES ── */}
      <section style={{ padding:'4rem 1.25rem', maxWidth:1100, margin:'0 auto' }}>
        <div style={{ textAlign:'center', marginBottom:'2.5rem' }}>
          <div style={{ color:'var(--green-600)', fontWeight:700, fontSize:12, textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:6 }}>Alur Proses</div>
          <h2 style={{ fontSize:'clamp(1.5rem,4vw,2rem)', color:'var(--green-900)' }}>Dari Hewan ke Tangan Penerima</h2>
        </div>
        <div className="steps-row">
          {[
            { num:'01', icon:'📋', title:'Pendaftaran', desc:'Data pengkurban & hewan dicatat admin' },
            { num:'02', icon:'🔪', title:'Penyembelihan', desc:'Proses sembelih dipantau real-time' },
            { num:'03', icon:'📦', title:'Packing', desc:'Daging dipotong & dikemas' },
            { num:'04', icon:'🚚', title:'Distribusi', desc:'Kurir antarkan ke penerima' },
            { num:'05', icon:'✅', title:'Selesai', desc:'Konfirmasi & laporan final' },
          ].map((step, i) => (
            <React.Fragment key={i}>
              <div className="card step-card">
                <div style={{ fontSize:28, marginBottom:6 }}>{step.icon}</div>
                <div style={{ fontSize:10, color:'var(--green-600)', fontWeight:700, marginBottom:3 }}>LANGKAH {step.num}</div>
                <div style={{ fontWeight:700, fontSize:13, color:'var(--green-900)', marginBottom:4 }}>{step.title}</div>
                <div style={{ fontSize:11, color:'var(--text-muted)', lineHeight:1.5 }}>{step.desc}</div>
              </div>
              {i < 4 && <div className="step-arrow">→</div>}
            </React.Fragment>
          ))}
        </div>
      </section>

      {/* ── FITUR ── */}
      <section style={{ padding:'3rem 1.25rem', background:'#fff' }}>
        <div style={{ maxWidth:1100, margin:'0 auto' }}>
          <div style={{ textAlign:'center', marginBottom:'2rem' }}>
            <h2 style={{ fontSize:'clamp(1.5rem,4vw,2rem)', color:'var(--green-900)' }}>Fitur Sistem</h2>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(300px,1fr))', gap:'1rem' }}>
            {features.map((f, i) => (
              <div key={i} className="card" style={{ display:'flex', gap:12 }}>
                <span style={{ fontSize:26, flex:'none' }}>{f.icon}</span>
                <div>
                  <div style={{ fontWeight:700, color:'var(--green-800)', marginBottom:3, fontSize:14 }}>{f.title}</div>
                  <div style={{ fontSize:13, color:'var(--text-muted)', lineHeight:1.6 }}>{f.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section style={{ padding:'4rem 1.25rem', background:'var(--green-900)', textAlign:'center' }}>
        <div style={{ maxWidth:580, margin:'0 auto' }}>
          <div className="arabic" style={{ fontSize:'clamp(18px,4vw,24px)', color:'#fbbf24', marginBottom:12 }}>
            وَمَا أَنفَقْتُم مِّن شَيْءٍ فَهُوَ يُخْلِفُهُ
          </div>
          <p style={{ color:'#86efac', fontSize:12, marginBottom:20 }}>Q.S. Saba' [34]: 39</p>
          <h2 style={{ fontSize:'clamp(1.4rem,4vw,2rem)', color:'#fff', marginBottom:'0.75rem' }}>Pantau Proses Qurban Sekarang</h2>
          <p style={{ color:'#a7d9b8', marginBottom:'1.75rem', fontSize:14 }}>Transparansi penuh dari hewan hingga distribusi daging ke penerima manfaat.</p>
          <div style={{ display:'flex', gap:10, justifyContent:'center', flexWrap:'wrap' }}>
            <Link to="/monitoring" className="btn btn-gold" style={{ padding:'13px 28px', fontSize:15 }}>
              Buka Monitoring
            </Link>
            <Link to="/pengkurban" className="btn" style={{ background:'rgba(255,255,255,0.1)', color:'#fff', border:'1px solid rgba(255,255,255,0.2)', padding:'13px 28px', fontSize:15 }}>
              <Users size={16} /> Cari Nama Kamu
            </Link>
          </div>
        </div>
      </section>

      <footer style={{ background:'#0f1a0f', padding:'1.25rem', textAlign:'center', color:'#4a6741', fontSize:12 }}>
        © 2026 Qurma — Sistem Monitoring Qurban Digital Permata
      </footer>
    </div>
  );
}
